import { derived, writable } from "svelte/store";
import { localUserStore } from "../Connection/LocalUserStore";
import {
    clampManualMicrophoneGain,
    DEFAULT_MANUAL_MICROPHONE_GAIN,
    DEFAULT_MICROPHONE_GAIN_MODE,
    type MicrophoneGainMode,
    type MicrophoneGainStatus,
} from "./MicrophoneGainConfig";

export type { MicrophoneGainMode, MicrophoneGainStatus } from "./MicrophoneGainConfig";

export interface MicrophoneGainState {
    mode: MicrophoneGainMode;
    effectiveMode: MicrophoneGainMode;
    manualGain: number;
    status: MicrophoneGainStatus;
    fallbackReason: string | null;
}

function createMicrophoneGainStore() {
    const initialMode = localUserStore.getMicrophoneGainMode();
    const initialManualGain = localUserStore.getMicrophoneManualGain();

    let currentState: MicrophoneGainState = {
        mode: initialMode,
        effectiveMode: initialMode,
        manualGain: initialManualGain,
        status: "idle",
        fallbackReason: null,
    };

    const { subscribe, set } = writable<MicrophoneGainState>(currentState);

    const setState = (nextState: MicrophoneGainState): void => {
        if (
            currentState.mode === nextState.mode &&
            currentState.effectiveMode === nextState.effectiveMode &&
            currentState.manualGain === nextState.manualGain &&
            currentState.status === nextState.status &&
            currentState.fallbackReason === nextState.fallbackReason
        ) {
            return;
        }

        currentState = nextState;
        set(currentState);
    };

    return {
        subscribe,

        setMode(mode: MicrophoneGainMode): void {
            localUserStore.setMicrophoneGainMode(mode);
            const isAlreadyEffective = currentState.effectiveMode === mode;
            setState({
                ...currentState,
                mode,
                status: isAlreadyEffective ? "active" : "idle",
                fallbackReason: null,
            });
        },

        setManualGain(value: number): void {
            const manualGain = clampManualMicrophoneGain(value);
            localUserStore.setMicrophoneManualGain(manualGain);
            setState({
                ...currentState,
                manualGain,
            });
        },

        setFallback(reason: string, effectiveMode: MicrophoneGainMode = DEFAULT_MICROPHONE_GAIN_MODE): void {
            setState({
                ...currentState,
                effectiveMode,
                status: "fallback",
                fallbackReason: reason,
            });
        },

        setUnavailable(reason: string, effectiveMode: MicrophoneGainMode = DEFAULT_MICROPHONE_GAIN_MODE): void {
            setState({
                ...currentState,
                effectiveMode,
                status: "unavailable",
                fallbackReason: reason,
            });
        },

        setActive(effectiveMode: MicrophoneGainMode): void {
            setState({
                ...currentState,
                effectiveMode,
                status: "active",
                fallbackReason: null,
            });
        },

        reset(): void {
            currentState = {
                mode: DEFAULT_MICROPHONE_GAIN_MODE,
                effectiveMode: DEFAULT_MICROPHONE_GAIN_MODE,
                manualGain: DEFAULT_MANUAL_MICROPHONE_GAIN,
                status: "idle",
                fallbackReason: null,
            };
            localUserStore.setMicrophoneGainMode(DEFAULT_MICROPHONE_GAIN_MODE);
            localUserStore.setMicrophoneManualGain(DEFAULT_MANUAL_MICROPHONE_GAIN);
            set(currentState);
        },
    };
}

export const microphoneGainStore = createMicrophoneGainStore();
export const selectedMicrophoneGainModeStore = derived(microphoneGainStore, ($microphoneGainStore) => {
    return $microphoneGainStore.mode;
});
export const effectiveMicrophoneGainModeStore = derived(microphoneGainStore, ($microphoneGainStore) => {
    return $microphoneGainStore.effectiveMode;
});
export const manualMicrophoneGainStore = derived(microphoneGainStore, ($microphoneGainStore) => {
    return $microphoneGainStore.manualGain;
});
